import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { TextArea, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useRef, useState } from "react";
import { useSendAcknowledgementLetterMutation } from "services/file-api.ts";
import { useLazyGetEmailTemplateQuery } from "services/email-api.ts";
import { extractErrorMessage } from "utils/error-utils";

import EmailAddressMultiSelect from "./EmailAddressMultiSelect";

const REFERRAL_ACKNOWLEDGEMENT_EMAIL_TYPE = "referral_acknowledgement";

// Deliberately permissive (just "something@something.something") since this
// only needs to catch obvious typos before hitting the backend, which does
// the real validation.
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

type AckEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  defaultToEmail?: string | null;
};

const AckEmailModal = ({
  isOpen,
  onClose,
  fileId,
  defaultToEmail,
}: AckEmailModalProps) => {
  const [toEmail, setToEmail] = useState(defaultToEmail ?? "");
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Remount Subject after rejecting an autofill write so the controlled
  // value wins again (ignoring onChange alone leaves the DOM stale).
  const [subjectInputKey, setSubjectInputKey] = useState(0);
  const [sendAcknowledgementLetter, { isLoading: isSending }] =
    useSendAcknowledgementLetterMutation();
  const [fetchEmailTemplate, { isFetching: isTemplateLoading }] =
    useLazyGetEmailTemplateQuery();

  // Tracks whether the user has typed a recipient/subject/body since the
  // modal last opened, so a late-arriving default or a recipient-triggered
  // template reload never overwrites something the user already typed.
  const hasEditedToEmailRef = useRef(false);
  const hasEditedSubjectRef = useRef(false);
  const hasEditedBodyRef = useRef(false);
  // Keyed on recipientEmail rather than a monotonic request id, so this
  // can't tell apart two in-flight loads for the *same* recipient (e.g. the
  // open-time fetch and a same-recipient reload racing). Not worth an id
  // for that: nothing in this modal re-fetches the same recipient twice in
  // quick succession today.
  const lastFetchedRecipientRef = useRef<string | undefined>(undefined);

  const loadTemplate = async (recipientEmail?: string) => {
    lastFetchedRecipientRef.current = recipientEmail;
    try {
      const template = await fetchEmailTemplate({
        emailType: REFERRAL_ACKNOWLEDGEMENT_EMAIL_TYPE,
        fileId,
        recipientEmail,
      }).unwrap();
      // A newer load (a different recipientEmail) may have started and
      // already finished while this one was in flight; applying this one
      // now would overwrite that fresher result with a stale one.
      if (lastFetchedRecipientRef.current !== recipientEmail) {
        return;
      }
      // Body doesn't depend on the recipient, but still don't clobber an
      // edit the user made between this load starting and finishing.
      if (!hasEditedBodyRef.current) {
        setBody(template.body);
      }
      // The subject is the only part that depends on the recipient (the
      // Liberty Mutual subject format); never overwrite a subject the user
      // already started editing, even one that arrives after they started.
      if (!hasEditedSubjectRef.current) {
        setSubject(template.subject);
      }
    } catch (error) {
      // Same staleness guard as the success path: a slower failed request
      // for an old recipient shouldn't toast an error over a newer load
      // that already succeeded.
      if (lastFetchedRecipientRef.current !== recipientEmail) {
        return;
      }
      console.error(error);
      KaboodleToaster.error(
        extractErrorMessage(error, "Failed to load the email template."),
      );
    }
  };

  // The parent only mounts this component while showAckEmailModal is true,
  // so it always mounts with isOpen already true -- starting this at false
  // (not isOpen) is what makes the first render register as "just opened"
  // below. Resets only on that open transition, not on every
  // defaultToEmail change, so a refetch while already open can't wipe out
  // in-progress typing.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justOpened) {
      hasEditedToEmailRef.current = false;
      hasEditedSubjectRef.current = false;
      hasEditedBodyRef.current = false;
      setSubjectInputKey(0);
      setToEmail(defaultToEmail ?? "");
      setCcEmails([]);
      setSubject("");
      setBody("");
      loadTemplate(defaultToEmail ?? undefined);
    } else if (isOpen && defaultToEmail && !hasEditedToEmailRef.current) {
      setToEmail(defaultToEmail);
      // fileData can still be loading when the modal first opens, so the
      // initial loadTemplate call above may have run with no recipient at
      // all (missing the Liberty Mutual subject check entirely). Reload
      // once the real default arrives, as long as the user hasn't started
      // editing the subject themselves and this isn't a repeat of the
      // recipient we already fetched for.
      if (
        !hasEditedSubjectRef.current &&
        defaultToEmail !== lastFetchedRecipientRef.current
      ) {
        loadTemplate(defaultToEmail);
      }
    }
  }, [isOpen, defaultToEmail]);

  const ccHasInvalidEmail = ccEmails.some(
    (email) => !EMAIL_PATTERN.test(email),
  );
  const hasInvalidToEmail = toEmail !== "" && !EMAIL_PATTERN.test(toEmail);

  const handleToEmailBlur = () => {
    if (
      hasInvalidToEmail ||
      !toEmail ||
      toEmail === lastFetchedRecipientRef.current
    ) {
      return;
    }
    loadTemplate(toEmail);
  };

  const handleSend = async () => {
    if (
      !toEmail ||
      hasInvalidToEmail ||
      ccHasInvalidEmail ||
      !subject ||
      !body
    ) {
      return;
    }

    try {
      await sendAcknowledgementLetter({
        id: fileId,
        to_email: toEmail,
        subject,
        body,
        cc_emails: ccEmails,
      }).unwrap();
      KaboodleToaster.success("Acknowledgement letter sent.");
      onClose();
    } catch (error) {
      console.error(error);
      KaboodleToaster.error(
        extractErrorMessage(
          error,
          "Failed to send acknowledgement letter. Please try again.",
        ),
      );
    }
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleSend}
            disabled={
              !toEmail ||
              hasInvalidToEmail ||
              ccHasInvalidEmail ||
              !subject ||
              !body ||
              isSending ||
              isTemplateLoading
            }
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Acknowledgement Email">
        {/* Chrome ignores autocomplete=off; picking a saved address also
            dumps it into Subject and would lock that in as a user edit. */}
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <TextInput
            name="ackEmailToEmail"
            label="Recipient Email"
            autoComplete="new-password"
            value={toEmail}
            onChange={(e) => {
              hasEditedToEmailRef.current = true;
              setToEmail(e.target.value);
            }}
            onBlur={handleToEmailBlur}
            required
          />
          {hasInvalidToEmail && (
            <div className="error-message">Enter a valid recipient email.</div>
          )}
          <EmailAddressMultiSelect
            name="ackEmailCcEmails"
            label="CC"
            value={ccEmails}
            onChange={setCcEmails}
          />
          <TextInput
            key={subjectInputKey}
            name="ackEmailSubject"
            label="Subject"
            autoComplete="new-password"
            value={isTemplateLoading ? "Loading..." : subject}
            readOnly={isTemplateLoading}
            onChange={(e) => {
              const next = e.target.value;
              // Autofill dumps a bare address here; ack subjects never are one.
              if (EMAIL_PATTERN.test(next.trim())) {
                setSubjectInputKey((key) => key + 1);
                return;
              }
              hasEditedSubjectRef.current = true;
              setSubject(next);
            }}
            required
          />
          <TextArea
            name="ackEmailBody"
            label="Body"
            value={isTemplateLoading ? "Loading..." : body}
            readOnly={isTemplateLoading}
            onChange={(e) => {
              hasEditedBodyRef.current = true;
              setBody(typeof e === "string" ? e : e.target.value);
            }}
            rows={16}
            required
          />
        </form>
      </Card>
    </KaboodleModal>
  );
};

export default AckEmailModal;
